import type { Edge, Node } from "@xyflow/react";
import { MarkerType } from "@xyflow/react";

/**
 * The workflow Steps editor's tree model + layout, ported from the old UI's
 * FlowBuilder (frontend/src/components/flows/FlowBuilder.tsx). Pure functions
 * so they're unit-testable without rendering the canvas.
 *
 * The graph is a TREE (not free-form): each step has at most one parent,
 * connectors are derived from a parentMap, and nodes aren't draggable or
 * connectable directly. Sibling branches fan out (run in parallel).
 */

export interface StepNodeData extends Record<string, unknown> {
  agentUuid?: string;
  agentName?: string;
  requiresReview: boolean;
  config?: Record<string, unknown>;
  label: string;
  /** A preview (Build with AI's draft canvas): no add/insert/delete controls. */
  readOnly?: boolean;
  onDelete?: (nodeId: string) => void;
  onAddChild?: (nodeId: string) => void;
  onInsertBefore?: (nodeId: string) => void;
}

export type StepNode = Node<StepNodeData, "step">;

export const NODE_X = 400;
export const NODE_H_GAP = 300;
export const NODE_V_GAP = 160;
export const NODE_Y_START = 60;

let nodeIdCounter = 0;
export function generateNodeId() {
  return `step-${Date.now()}-${nodeIdCounter++}`;
}

/** The serializable shape saved to the API. */
export interface BuilderStep {
  agent_uuid?: string;
  step_type?: string;
  instructions?: string;
  expected_output?: string;
  config?: Record<string, unknown>;
  requires_review?: boolean;
}
export interface BuilderConnector {
  from_step_order?: number;
  to_step_order: number;
  label?: string;
}

export function buildParentMapFromConnectors(
  initNodes: StepNode[],
  initialConnectors: Array<{ fromStepId?: number; toStepId: number }>,
  dbIdToNodeId: Map<number, string>,
): Map<string, string | null> {
  const pm = new Map<string, string | null>();
  if (initialConnectors.length > 0) {
    for (const node of initNodes) pm.set(node.id, null);
    for (const conn of initialConnectors) {
      const toNodeId = dbIdToNodeId.get(conn.toStepId);
      const fromNodeId = conn.fromStepId != null ? dbIdToNodeId.get(conn.fromStepId) : null;
      if (toNodeId) pm.set(toNodeId, fromNodeId ?? null);
    }
  } else {
    let prevId: string | null = null;
    for (const node of initNodes) {
      pm.set(node.id, prevId);
      prevId = node.id;
    }
  }
  return pm;
}

/** Lay the tree out: roots centered/spread, children centered under parents. */
export function computeTreeLayout(
  nodes: StepNode[],
  parentMap: Map<string, string | null>,
): Map<string, { x: number; y: number }> {
  const childrenOf = new Map<string | null, string[]>();
  for (const node of nodes) {
    const pid = parentMap.get(node.id) ?? null;
    if (!childrenOf.has(pid)) childrenOf.set(pid, []);
    childrenOf.get(pid)?.push(node.id);
  }

  const positions = new Map<string, { x: number; y: number }>();
  const subtreeWidth = new Map<string, number>();

  function getWidth(nodeId: string): number {
    const children = childrenOf.get(nodeId) || [];
    if (children.length === 0) {
      subtreeWidth.set(nodeId, 1);
      return 1;
    }
    const total = children.reduce((sum, cid) => sum + getWidth(cid), 0);
    subtreeWidth.set(nodeId, total);
    return total;
  }

  function assignPositions(nodeId: string, centerX: number, depth: number) {
    positions.set(nodeId, { x: centerX, y: NODE_Y_START + depth * NODE_V_GAP });
    const children = childrenOf.get(nodeId) || [];
    if (children.length === 0) return;
    const totalChildWidth = children.reduce((s, cid) => s + (subtreeWidth.get(cid) || 1), 0);
    let offsetX = centerX - ((totalChildWidth - 1) * NODE_H_GAP) / 2;
    for (const childId of children) {
      const w = subtreeWidth.get(childId) || 1;
      assignPositions(childId, offsetX + ((w - 1) * NODE_H_GAP) / 2, depth + 1);
      offsetX += w * NODE_H_GAP;
    }
  }

  const roots = childrenOf.get(null) || [];
  for (const rid of roots) getWidth(rid);
  if (roots.length === 1) {
    assignPositions(roots[0] as string, NODE_X, 0);
  } else {
    let x = NODE_X;
    for (const rid of roots) {
      const w = subtreeWidth.get(rid) || 1;
      assignPositions(rid, x, 0);
      x += w * NODE_H_GAP + NODE_H_GAP;
    }
  }
  return positions;
}

/** Edges derived from the parentMap (neutral animated smoothstep, like the rest of the app). */
const EDGE_COLOR = "var(--color-border-strong)";

export function buildTreeEdges(nodes: StepNode[], parentMap: Map<string, string | null>): Edge[] {
  const edges: Edge[] = [];
  for (const node of nodes) {
    const pid = parentMap.get(node.id);
    if (pid) {
      edges.push({
        id: `e-${pid}-${node.id}`,
        source: pid,
        target: node.id,
        type: "smoothstep",
        animated: true,
        style: { stroke: EDGE_COLOR, strokeWidth: 2 },
        markerEnd: { type: MarkerType.ArrowClosed, color: EDGE_COLOR, width: 20, height: 20 },
      });
    }
  }
  return edges;
}

/** Deepest leaf down the rightmost path — where "Add step" chains to. */
export function findLastLeaf(nodes: StepNode[], parentMap: Map<string, string | null>): string | null {
  if (nodes.length === 0) return null;
  const childrenOf = new Map<string | null, string[]>();
  for (const [nid, pid] of parentMap.entries()) {
    if (!childrenOf.has(pid)) childrenOf.set(pid, []);
    childrenOf.get(pid)?.push(nid);
  }
  const roots = childrenOf.get(null) || [];
  if (roots.length === 0) return nodes.at(-1)?.id ?? null;
  let current = roots.at(-1) as string;
  for (;;) {
    const children = childrenOf.get(current) || [];
    if (children.length === 0) return current;
    current = children.at(-1) as string;
  }
}

/** Ids of a node and all its descendants (for delete and cycle prevention). */
export function collectSubtree(rootId: string, parentMap: Map<string, string | null>): Set<string> {
  const out = new Set<string>();
  const queue = [rootId];
  while (queue.length > 0) {
    const cur = queue.pop() as string;
    out.add(cur);
    for (const [cid, pid] of parentMap.entries()) {
      if (pid === cur && !out.has(cid)) queue.push(cid);
    }
  }
  return out;
}

/** Breadth-first serialization to the API's ordered steps + connectors. */
export function serializeSteps(
  nodes: StepNode[],
  parentMap: Map<string, string | null>,
): { steps: BuilderStep[]; connectors: BuilderConnector[] } {
  const childrenOf = new Map<string | null, string[]>();
  for (const [nid, pid] of parentMap.entries()) {
    if (!childrenOf.has(pid)) childrenOf.set(pid, []);
    childrenOf.get(pid)?.push(nid);
  }
  const nodeById = new Map(nodes.map((n) => [n.id, n]));
  const steps: BuilderStep[] = [];
  const connectors: BuilderConnector[] = [];
  const orderMap = new Map<string, number>();

  const queue: string[] = [...(childrenOf.get(null) || [])];
  while (queue.length > 0) {
    const nid = queue.shift() as string;
    const node = nodeById.get(nid);
    if (!node) continue;
    const stepOrder = steps.length;
    orderMap.set(nid, stepOrder);
    steps.push({
      agent_uuid: node.data.agentUuid,
      step_type: node.data.agentUuid ? "agent" : "prompt",
      instructions: typeof node.data.config?.prompt === "string" ? node.data.config.prompt : undefined,
      expected_output:
        typeof node.data.config?.expected_output === "string" ? node.data.config.expected_output : undefined,
      config: { ...node.data.config, label: node.data.label || undefined },
      requires_review: node.data.requiresReview,
    });
    const pid = parentMap.get(nid);
    if (pid != null) connectors.push({ from_step_order: orderMap.get(pid), to_step_order: stepOrder });
    queue.push(...(childrenOf.get(nid) || []));
  }
  return { steps, connectors };
}
